/**
 * v6.32.116 — Universal Sales-Rep Demo Launcher.
 *
 * The single page rendered for the role `sales_rep_universal`. Shows
 * four big role cards. Click → POST /sales-rep/switch-role → swap
 * localStorage session token → land in the chosen role's dashboard.
 */
import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Building2, School, GraduationCap, BookOpen,
  ArrowRight, LogOut, Sparkles, MapPin, Mail, Phone,
  FileText, Download, Lock, Eye, Handshake,
} from "lucide-react";
import { toast } from "sonner";
import AuthContext from "@/contexts/AuthContext";
import nycRepDirectory from "@/data/nyc_rep_directory.json";
import salesCollateral from "@/data/sales_collateral.json";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_ICONS = {
  district_admin: Building2,
  school_admin: School,
  teacher: GraduationCap,
  student: BookOpen,
};

const ROLE_DEST = {
  district_admin: "/admin/district",
  school_admin: "/admin/school",
  teacher: "/teacher",
  student: "/my-assessment",
};

const ROLE_THEME = {
  district_admin: { ring: "ring-blue-300",  bg: "bg-blue-50",  ic: "text-blue-700"   },
  school_admin:   { ring: "ring-emerald-300", bg: "bg-emerald-50", ic: "text-emerald-700" },
  teacher:        { ring: "ring-amber-300", bg: "bg-amber-50", ic: "text-amber-700"  },
  student:        { ring: "ring-rose-300",  bg: "bg-rose-50",  ic: "text-rose-700"   },
};

const DemoLauncher = () => {
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useContext(AuthContext);
  const [roles, setRoles] = useState([]);
  const [busy, setBusy] = useState(null);
  const [meEmail, setMeEmail] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/sales-rep/me`);
        if (cancelled) return;
        setRoles(r.data?.available_roles || []);
        setMeEmail(r.data?.user?.email || "");
      } catch (e) {
        toast.error("This page is only for the universal sales-rep account.");
        navigate("/login");
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const switchTo = async (role) => {
    if (busy) return;
    setBusy(role);
    try {
      const r = await axios.post(`${API}/sales-rep/switch-role`, { role });
      const newToken = r.data?.session_token;
      if (!newToken) throw new Error("No session token returned");
      // Swap the auth token. Existing axios default header is set by AuthContext on next refresh.
      localStorage.setItem("mosaic_session_token", newToken);
      // Also update axios default for the immediate next request
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
      // Refresh AuthContext so the rest of the app picks up the new role
      if (checkAuth) await checkAuth();
      toast.success(`Now viewing as ${role.replace("_", " ")}`);
      navigate(ROLE_DEST[role] || "/");
    } catch (e) {
      toast.error(`Switch failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const handleLogout = async () => {
    try { await logout?.(); } catch { /* ignore */ }
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 px-6 py-12" data-testid="demo-launcher-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-10 flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 border-2 border-amber-300 text-amber-800 text-[11px] font-bold uppercase tracking-wider mb-3">
              <Sparkles size={12} /> Sales Demo
            </div>
            <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              MOSAIC Demo Launcher
            </h1>
            <p className="text-gray-600 mt-2 max-w-xl">
              Pick the role you want to demo. Every account uses sealed
              fake data — no real student or customer information appears.
            </p>
            {meEmail && (
              <p className="text-xs text-gray-400 mt-2">Signed in as <span className="font-mono">{meEmail}</span></p>
            )}
          </div>
          <Button variant="outline" onClick={handleLogout} className="btn-sharp h-9" data-testid="demo-launcher-logout">
            <LogOut size={14} className="mr-1.5" /> Sign out
          </Button>
        </div>

        {/* v6.37.14 — Sales Playbook banner: full 3-part playbook
            (Demo Script + Audience Guides + Sales Training) reachable
            in one click. This is the "everything a rep needs to
            qualify, present, handle objections, and close" surface. */}
        <button
          type="button"
          onClick={() => navigate("/rep-playbook")}
          className="w-full text-left bg-gradient-to-r from-[#002FA7] to-[#0EA5E9] hover:from-[#001B70] hover:to-[#0284C7] text-white p-6 mb-6 transition-all group flex items-center justify-between gap-6"
          data-testid="rep-playbook-link"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/15 flex items-center justify-center">
              <Handshake size={30} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-widest opacity-80 mb-1">
                PART III · SALES TEAM TRAINING & CALL PLAYBOOK
              </div>
              <div className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                Open the Rep Playbook
              </div>
              <div className="text-sm opacity-85 mt-0.5">
                Feature cheat sheet · Buyer personas · 30-min call framework ·
                Objection handlers · Discovery questions · Competitive matrix ·
                Post-call checklist
              </div>
            </div>
          </div>
          <ArrowRight size={28} className="opacity-80 group-hover:translate-x-1 transition-transform flex-shrink-0" />
        </button>

        {/* Role cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {roles.length === 0 ? (
            <div className="col-span-2 text-center py-12 text-gray-400">Loading demo roles…</div>
          ) : roles.map((r) => {
            const Icon = ROLE_ICONS[r.role] || Sparkles;
            const theme = ROLE_THEME[r.role] || ROLE_THEME.teacher;
            const loading = busy === r.role;
            return (
              <button
                key={r.role}
                type="button"
                disabled={!!busy}
                onClick={() => switchTo(r.role)}
                className={`text-left bg-white border-2 border-gray-200 hover:border-gray-400 p-6 transition-all hover:shadow-lg ${loading ? "opacity-60" : ""} ${theme.ring} hover:ring-2 group`}
                data-testid={`demo-launcher-card-${r.role}`}
              >
                <div className="flex items-start justify-between">
                  <div className={`p-3 ${theme.bg}`}>
                    <Icon size={28} className={theme.ic} />
                  </div>
                  <ArrowRight size={20} className="text-gray-400 group-hover:text-gray-700 group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mt-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {r.label}
                </h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{r.blurb}</p>
                <div className="text-[11px] text-gray-400 mt-4 uppercase tracking-wide font-semibold">
                  {loading ? "Switching…" : "View as " + r.label}
                </div>
              </button>
            );
          })}
        </div>

        {/* NYC Territory Coverage — one card per borough */}
        <div className="mt-12" data-testid="nyc-territory-section">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              NYC Territory Coverage
            </h2>
            <span className="text-xs text-gray-400 uppercase tracking-wide font-semibold">
              5 boroughs · all NYCPS districts
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {(nycRepDirectory.boroughs || []).map((b) => {
              const name  = b.coordinator || nycRepDirectory.shared_contact?.name;
              const email = b.email       || nycRepDirectory.shared_contact?.email;
              const phone = b.phone       || nycRepDirectory.shared_contact?.phone;
              return (
                <div
                  key={b.abbr}
                  className="bg-white border-2 border-gray-200 p-4 hover:border-blue-400 transition-colors"
                  data-testid={`nyc-borough-card-${b.abbr}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin size={14} className="text-blue-700" />
                    <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wide">{b.borough}</h3>
                  </div>
                  <div className="text-[10.5px] text-gray-500 mb-3 leading-snug">
                    Districts {b.districts.join(", ")}
                  </div>
                  <div className="text-xs text-gray-700 font-semibold mb-1 truncate" title={name}>
                    {name}
                  </div>
                  {email && (
                    <a
                      href={`mailto:${email}?subject=MOSAIC%20quote%20request%20—%20${encodeURIComponent(b.borough)}`}
                      className="flex items-center gap-1.5 text-[11px] text-blue-700 hover:underline mb-0.5 break-all"
                      data-testid={`nyc-borough-email-${b.abbr}`}
                    >
                      <Mail size={11} className="flex-shrink-0" />
                      <span className="truncate">{email}</span>
                    </a>
                  )}
                  {phone && (
                    <a
                      href={`tel:${phone.replace(/[^0-9+]/g, "")}`}
                      className="flex items-center gap-1.5 text-[11px] text-blue-700 hover:underline"
                      data-testid={`nyc-borough-phone-${b.abbr}`}
                    >
                      <Phone size={11} className="flex-shrink-0" />
                      <span>{phone}</span>
                    </a>
                  )}
                </div>
              );
            })}
          </div>
          {nycRepDirectory.shared_contact?.hours && (
            <p className="text-[11px] text-gray-400 mt-3">
              Territory Desk hours: {nycRepDirectory.shared_contact.hours}
            </p>
          )}
        </div>

        {/* Sales Collateral — downloadable rep assets */}
        <div className="mt-12" data-testid="sales-collateral-section">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Sales Collateral
            </h2>
            <span className="text-xs text-gray-400 uppercase tracking-wide font-semibold">
              {(salesCollateral.collateral || []).length} assets · Flyers · Battle card · Checklists
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(salesCollateral.collateral || []).map((c) => {
              const isInternal = c.audience === "internal";
              return (
                <div
                  key={c.id}
                  className={`bg-white border-2 p-5 transition-all hover:shadow-md ${isInternal ? "border-amber-300 hover:border-amber-500" : "border-blue-200 hover:border-blue-400"}`}
                  data-testid={`collateral-card-${c.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className={`p-2 ${isInternal ? "bg-amber-50" : "bg-blue-50"}`}>
                      {isInternal
                        ? <Lock size={18} className="text-amber-700" />
                        : <FileText size={18} className="text-blue-700" />}
                    </div>
                    {isInternal && (
                      <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-300 px-2 py-0.5">
                        Rep only
                      </span>
                    )}
                  </div>
                  <h3 className="font-bold text-gray-900 text-base leading-snug mb-1">{c.title}</h3>
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold mb-2">{c.subtitle}</p>
                  <p className="text-xs text-gray-700 leading-relaxed mb-4">{c.description}</p>
                  <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
                    <span className="text-[10px] text-gray-400 font-mono">{c.size_label}</span>
                    <div className="flex items-center gap-1.5">
                      {c.preview && (
                        <a
                          href={c.preview}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-gray-600 hover:text-gray-900 px-2 py-1 border border-gray-300 hover:border-gray-500 transition-colors"
                          data-testid={`collateral-preview-${c.id}`}
                        >
                          <Eye size={11} /> Preview
                        </a>
                      )}
                      <a
                        href={c.file}
                        target="_blank"
                        rel="noreferrer"
                        download
                        className={`inline-flex items-center gap-1 text-[11px] font-semibold text-white px-3 py-1 transition-colors ${isInternal ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-700 hover:bg-blue-800"}`}
                        data-testid={`collateral-download-${c.id}`}
                      >
                        <Download size={11} /> Download
                      </a>
                    </div>
                  </div>
                  {Array.isArray(c.variants) && c.variants.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-dashed border-gray-200" data-testid={`collateral-variants-${c.id}`}>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">
                        Personalized rep variants
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {c.variants.map((v) => (
                          <a
                            key={v.rep}
                            href={v.file}
                            target="_blank"
                            rel="noreferrer"
                            download
                            className="inline-flex items-center gap-1 text-[10px] text-blue-700 hover:text-white hover:bg-blue-700 px-2 py-1 border border-blue-300 transition-colors"
                            data-testid={`collateral-variant-${c.id}-${v.rep.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <Download size={9} /> {v.rep}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-400 mt-3">
            <span className="inline-block w-2 h-2 bg-amber-500 mr-1 align-middle"></span>
            <b>Rep-only</b> materials must never be forwarded to prospects.
            <span className="inline-block w-2 h-2 bg-blue-500 mr-1 ml-3 align-middle"></span>
            <b>Public</b> materials are cleared for direct sharing with schools.
          </p>
        </div>

        {/* Footer note */}
        <div className="mt-10 p-4 bg-gray-50 border border-gray-200 text-xs text-gray-500 max-w-3xl">
          <strong className="text-gray-700">How this works:</strong> When you pick a role you'll be
          signed into a pre-seeded demo account scoped to MOSAIC Demo District (60 fake students across
          2 schools). To return to this launcher, sign out and sign back in with the universal demo email.
        </div>
      </div>
    </div>
  );
};

export default DemoLauncher;
